import { Input, Text } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { type InputBaseComponentProps, Stack } from "@mui/material";
import { extractPhoneNumberDigits } from "@src/appV2/lib";
import { MaskedPhoneInput } from "@src/appV2/lib/FormFields/PhoneField/MaskedPhoneInput";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { phoneFormSchema } from "@src/appV2/redesign/Profile/types/phoneFormSchema";
import { type FunctionComponent, useEffect, useRef, useState } from "react";
import { FormProvider, useController, useForm } from "react-hook-form";

interface EnterPhoneStepProps {
  onPhoneEntered: (phone: string) => void;
}

// PhoneField wrapper that only shows "already in use" error
function PhoneFieldWithFilteredErrors({ name }: { name: string }) {
  const [isFocused, setIsFocused] = useState(false);

  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({
    name,
  });

  // Only show error if it's the "already in use" message (not the length validation)
  const shouldShowError = error?.message?.includes("already in use");
  const displayError = shouldShowError ? error : undefined;

  return (
    <Stack spacing={1}>
      <Input
        {...field}
        fullWidth
        type="tel"
        inputProps={{
          "aria-label": "Phone number",
        }}
        placeholder={isFocused ? "(123) 456 - 7890" : "Enter your phone number"}
        disabled={isSubmitting}
        size="large"
        error={Boolean(displayError)}
        inputComponent={MaskedPhoneInput as unknown as FunctionComponent<InputBaseComponentProps>}
        iconType="phone"
        sx={
          displayError
            ? {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => `${theme.palette.error.main} !important`,
                },
              }
            : undefined
        }
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          field.onBlur();
        }}
      />
      {displayError && (
        <Text variant="caption" color="error" sx={{ paddingLeft: 1 }}>
          {displayError.message}
        </Text>
      )}
    </Stack>
  );
}

export function EnterPhoneStep({ onPhoneEntered }: EnterPhoneStepProps) {
  const formRef = useRef<HTMLFormElement>(null);

  const formMethods = useForm({
    defaultValues: {
      phone: "",
    },
    resolver: zodResolver(phoneFormSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    trigger,
    formState: { isSubmitting, isValid },
  } = formMethods;

  const phoneValue = watch("phone");

  // Only trigger validation when phone is complete (10 digits)
  // This way length errors never get triggered/displayed
  useEffect(() => {
    if (!phoneValue) {
      return;
    }

    const digits = phoneValue.replace(/\D/g, "");

    // Only run validation if phone is complete (10 digits)
    if (digits.length === 10) {
      void trigger("phone");
    }
  }, [phoneValue, trigger]);

  const onSubmit = (formData: { phone: string }) => {
    const phoneDigits = extractPhoneNumberDigits(formData.phone);
    onPhoneEntered(phoneDigits);
  };

  return (
    <FormProvider {...formMethods}>
      <Stack
        ref={formRef}
        component="form"
        sx={{ paddingY: 4, gap: 12 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={6} marginBottom={12}>
          <Text variant="body2">Enter your new phone number.</Text>
          <PhoneFieldWithFilteredErrors name="phone" />
        </Stack>
        <Stack spacing={2} sx={{ marginTop: 12 }}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            isLoading={isSubmitting}
            disabled={!isValid}
          >
            Continue
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
